// Project card API — обёртка над эндпоинтами /project-cards/{entityTypeId}/{itemId}/*
// Типы соответствуют моделям tasks_module_internal_models.* из swagger.
import { http } from '@/shared/api/http';
import type { DeepRequired, Schema, WithOptional } from '@/shared/api';

// --- Общие сущности ---
export type ProjectCardUser = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectCardUser'>
>;

export type ProjectCardClient = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectCardClient'>
>;

export interface ProjectTimeStats {
  declaredSeconds: number;
  spentSeconds: number;
  overrunSeconds: number;
  usagePercent: number;
}

export type ProjectTabCounters = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectTabCounters'>
>;

// Готовые агрегаты по проекту (бэкенд считает сам).
export type ProjectAggregates = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.ProjectAggregates'>>,
  'hasData'
>;

// --- Поля проекта для вкладки «Данные» (раньше брались из /projects/list) ---
export interface SummaryCompany {
  id?: number;
  title?: string;
}

// Контакт в summary: ФИО приходят раздельно, должность — post (не workPosition),
// телефоны — массив phones (не одна строка phone).
export interface SummaryContact {
  id?: number;
  name?: string;
  lastName?: string;
  secondName?: string;
  post?: string;
  phones?: string[];
}

export type SummaryCustomField = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.CustomField'>>,
  'name' | 'label' | 'type' | 'value'
>;

// --- Шапка карточки проекта ---
export interface ProjectCardSummary {
  projectId: number;
  entityTypeId: number;
  title: string;
  direction: string;
  status: string;
  statusName: string;
  completionPercent: number;
  deadline: string;
  // Дата перехода проекта на завершённый этап (ISO8601). null/пусто, пока проект
  // не закрыт. Если задана — срок фиксируется на этот момент (см. projectDaysLeft).
  movedAt?: string | null;
  timeExtension: number;
  client: ProjectCardClient;
  teamLead: ProjectCardUser;
  timeStats: ProjectTimeStats;
  tabCounters: ProjectTabCounters;
  aggregates: ProjectAggregates;
  tasksByAssignee: Record<string, number>;
  tasksByStatus: Record<string, number>;
  alerts: string[];
  // Поля для вкладки «Данные» — теперь приходят в summary (без /projects/list).
  stageName?: string;
  lastMessage?: string;
  dateStart?: string;
  dateFinish?: string;
  createdTime?: string;
  bitrixUrl?: string;
  company?: SummaryCompany;
  contacts?: SummaryContact[];
  customFields?: SummaryCustomField[];
}

// --- Команда ---
export type ProjectDept = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectDept'>
>;

export type ProjectTeamUser = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectTeamUser'>
>;

// Корзины задач по статусам — для диаграмм списка и детали.
export type StatusCounts = DeepRequired<
  Schema<'tasks_module_internal_models.StatusCounts'>
>;

export type TeamMemberMetrics = DeepRequired<
  Schema<'tasks_module_internal_models.TeamMemberMetrics'>
>;

export type TeamMemberItem = DeepRequired<
  Schema<'tasks_module_internal_models.TeamMemberItem'>
>;

export type ProjectTeamResponse = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectTeamResponse'>
>;

// --- Результаты ---
export type ProjectResultFile = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectResultFile'>
>;

export type ProjectResult = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.ProjectResult'>>,
  'authorName' | 'authorAvatar' | 'files'
>;

export type ProjectResultGroup = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectResultGroup'>
>;

export type ProjectResultsResponse = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectResultsResponse'>
>;

// --- Файлы (Данные) ---
export type ProjectFileItem = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectFileItem'>
>;

export type SmartProcessFileGroup = DeepRequired<
  Schema<'tasks_module_internal_models.SmartProcessFileGroup'>
>;

export type TaskFileGroup = DeepRequired<
  Schema<'tasks_module_internal_models.TaskFileGroup'>
>;

// Файл из бакетов categorizedFiles (FileDTO бэка).
export interface CategorizedFileItem {
  id: number;
  name: string;
  size?: number;
  downloadUrl?: string;
  url?: string;
  mimeType?: string;
  isImage?: boolean;
}

export type ProjectDataResponse = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.ProjectDataResponse'>>,
  'categorizedFiles'
>;

// --- Таймлайн (формат чата) ---
export interface ProjectTimelineMessage {
  id: number;
  chatId?: number;
  authorId: number;
  commentId?: number;
  date: string;
  text: string;
  isSystem: boolean;
}

export type ProjectTimelineResponse = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectTimelineResponse'>
>;

// --- Реквизиты доступа клиента ---
export type ProjectAccessResponse = Record<string, ANY>;

// --- Задачи проекта (для таба «Задачи») — из /tasks/list ---
export interface ProjectTaskItem {
  ID: number;
  Title: string;
  Deadline: string;
  DurationPlan: number;
  DurationFact: number;
  TimeEstimate: number;
  Status: number;
  ResponsibleID: number;
  GroupID: number;
  accomplices: number[];
  needsAttention: boolean;
  /** `/tasks/list` отдаёт ProjectShort — ID/EntityTypeID/Title, как в спеке.
   *  Форма `{ entityID, id, title }` здесь была устаревшей и никем не читалась. */
  project?: Schema<'tasks_module_internal_models.ProjectShort'>;
}

// --- API ---
const base = (entityTypeId: number, itemId: number) =>
  `/project-cards/${entityTypeId}/${itemId}`;

export const fetchProjectCardSummary = async (
  entityTypeId: number,
  itemId: number
): Promise<ProjectCardSummary> => {
  const { data } = await http.get(base(entityTypeId, itemId));
  return data;
};

export const fetchProjectTeam = async (
  entityTypeId: number,
  itemId: number
): Promise<ProjectTeamResponse> => {
  const { data } = await http.get(`${base(entityTypeId, itemId)}/team`);
  return data;
};

export const fetchProjectResults = async (
  entityTypeId: number,
  itemId: number
): Promise<ProjectResultsResponse> => {
  const { data } = await http.get(`${base(entityTypeId, itemId)}/results`);
  return data;
};

export const fetchProjectData = async (
  entityTypeId: number,
  itemId: number,
  source: 'smart' | 'task' | 'all' = 'all'
): Promise<ProjectDataResponse> => {
  const { data } = await http.get(`${base(entityTypeId, itemId)}/data`, {
    params: { source },
  });
  return data;
};

export const fetchProjectTimeline = async (
  entityTypeId: number,
  itemId: number,
  params?: { cursor?: string; limit?: number }
): Promise<ProjectTimelineResponse> => {
  const { data } = await http.get(`${base(entityTypeId, itemId)}/timeline`, {
    params,
  });
  return data;
};

export const fetchProjectAccess = async (
  entityTypeId: number,
  itemId: number
): Promise<ProjectAccessResponse> => {
  const { data } = await http.get(`${base(entityTypeId, itemId)}/access`);
  return data;
};

// Задачи проекта — /tasks/list по проекту (грузим все). Используется вкладкой
// «Результаты» для резолва задачи по taskId (дедлайн/ответственный).
export const fetchProjectCardTasks = async (
  entityTypeId: number,
  itemId: number
): Promise<ProjectTaskItem[]> => {
  const { data } = await http.get('/tasks/list', {
    params: {
      PROJECT_ENTITY_TYPE_ID: entityTypeId,
      PROJECT_ENTITY_ID: itemId,
      limit: 1000,
    },
  });
  return Array.isArray(data?.result) ? data.result : [];
};

// --- Пагинированный список задач (вкладка «Задачи», infinite scroll) ---
// Лёгкая ручка GET /project-cards/{entityTypeId}/{itemId}/tasks — скоуп по
// проекту уже в пути, поля под карточку (см. .kb/project-tasks-tab-backend-spec.md).
export interface ProjectTasksQuery {
  search?: string;
  departmentId?: number;
  responsibleId?: number;
  status?: number; // 5 — завершено, 6 — отложено
  overdue?: boolean;
  help?: boolean;
  excludeCompleted?: boolean;
}

// Если ни status, ни excludeCompleted не переданы — бэк сам подставляет
// excludeCompleted=true (показывает незавершённые).
export interface ProjectCardTaskListItem {
  id: number;
  title: string;
  deadline?: string;
  declaredSeconds: number;
  spentSeconds: number;
  responsibleId: number;
  accomplicesCount: number;
}

export interface ProjectTasksPage {
  result: ProjectCardTaskListItem[];
  total: number;
  hasMore: boolean;
}

export const fetchProjectCardTasksPage = async (
  entityTypeId: number,
  itemId: number,
  q: ProjectTasksQuery & { page: number; limit: number }
): Promise<ProjectTasksPage> => {
  const { data } = await http.get(`${base(entityTypeId, itemId)}/tasks`, {
    params: {
      page: q.page,
      limit: q.limit,
      ...(q.search ? { search: q.search } : {}),
      ...(q.departmentId ? { departmentId: q.departmentId } : {}),
      ...(q.responsibleId ? { responsibleId: q.responsibleId } : {}),
      ...(q.status ? { status: q.status } : {}),
      ...(q.overdue ? { overdue: true } : {}),
      ...(q.help ? { help: true } : {}),
      ...(q.excludeCompleted ? { excludeCompleted: true } : {}),
    },
  });
  return {
    result: Array.isArray(data?.data) ? data.data : [],
    total: Number(data?.total ?? 0),
    hasMore: Boolean(data?.hasMore),
  };
};

// --- Диаграмма «Часы по сотрудникам» (план/факт), вкладка «Задачи» ---
// GET /project-cards/{entityTypeId}/{itemId}/employee-hours — за всё время
// проекта, только участники с ненулевым planHours/factHours.
export interface ProjectEmployeeHoursRow {
  userId: number;
  name: string;
  planHours: number;
  factHours: number;
}

export const fetchProjectEmployeeHours = async (
  entityTypeId: number,
  itemId: number
): Promise<ProjectEmployeeHoursRow[]> => {
  const { data } = await http.get(
    `${base(entityTypeId, itemId)}/employee-hours`
  );
  return Array.isArray(data?.rows) ? data.rows : [];
};

// --- Диаграммы (charts) — серверная агрегация по 5 статусам за окно дней ---
export type ProjectChartsDepartment = DeepRequired<
  Schema<'tasks_module_internal_models.ChartDepartment'>
>;

export type ProjectChartsResponse = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectChartsResponse'>
>;

// GET /project-cards/{entityTypeId}/{itemId}/charts?days=N
export const fetchProjectCharts = async (
  entityTypeId: number,
  itemId: number,
  days: number
): Promise<ProjectChartsResponse> => {
  const { data } = await http.get(`${base(entityTypeId, itemId)}/charts`, {
    params: { days },
  });
  return data;
};
